<template>
  <div class="directDaySelector">
    <div v-for="month in datesInMonths" class="directDaySelector__month-and-dates">
      <div class="month">{{ month.month.format("MMMM") }}</div>
      <div class="directDaySelector__month-and-dates__dates">
        <div v-for="date in month.dates"
             class="day"
             @click="toggle(date)"
             :class="{
               'isSelectable': date.isSelectable,
               'isWeekend': date.isWeekend,
               'isInRange': date.isInRange,
               'isSelected': date.isSelected
             }">
          {{ date.format("D") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'direct-day-selector',
  description: 'Can list individual days of a date range in month rows.\n' +
    'Each day can be selected, this emits two events:\n' +
    ' "toggled" : has the date and whether it is selected\n' +
    ' "updated" : has counts and a list of selected dates\n',
  props: {
    fromDate: {
      default () {
        return moment()
      },
      validator: function (value) {
        return moment(value).isValid()
      }
    },
    toDate: {
      default () {
        return moment().add(1, 'month')
      },
      validator: function (value) {
        return moment(value).isValid()
      }
    },
    selectedDays: {
      default () {
        return []
      },
      type: Array
    },
    includeWeekends: {
      default () {
        return false
      },
      type: Boolean
    }
  },
  data () {
    return {
      dates: [],
      datesInMonths: [],
      checked: false
    }
  },
  watch: {
    fromDate: function (newVal, oldVal) {
      this.updateDates()
    },
    toDate: function (newVal, oldVal) {
      this.updateDates()
    },
    includeWeekends: function (newVal, oldVal) {
      this.updateDates()
    },
    selectedDays: function (newVal, oldVal) {
      this.updateDates()
    }
  },
  computed: {
    totalDaysCount () {
      return this.dates.length
    },
    selectableDaysCount () {
      return this.dates.filter(function (date) {
        return date.isSelectable
      }).length
    },
    selectedDaysCount () {
      let self = this
      return this.selectedDays.filter(function (date) {
        return (self.fromDate <= moment(date) && moment(date) <= self.toDate)
      }).length
    }
  },
  methods: {
    updateDates () {
      this.datesInMonths = [] /* {month: '08-2020', dates: ['dato1', 'dato2']} */
      this.dates = []
      if (this.fromDate < this.toDate) {
        let theDate = this.fromDate.clone().startOf('month')
        while (theDate.clone().endOf('day') <= this.toDate.clone().endOf('month')) { // create dates in the calendar
          let thisDate = theDate.clone().endOf('day')
          if (theDate.format('D') === '1') {
            let monthObj = {month: moment(theDate.format('YYYY-MM')), dates: []}
            this.datesInMonths.push(monthObj)
          }
          thisDate.simpleDate = thisDate.format('YYYY-MM-DD')
          thisDate.isWeekend = this.isWeekend(thisDate)
          thisDate.isInRange = this.isInRange(thisDate)
          thisDate.isSelectable = this.includeWeekends ? thisDate.isInRange : (!thisDate.isWeekend && thisDate.isInRange)
          // thisDate.isSelectable = thisDate.isInRange
          thisDate.isSelected = thisDate.isSelectable && this.selectedDays.indexOf(thisDate.simpleDate) > -1
          let idx = this.datesInMonths.findIndex(month => moment(month.month).format('YYYY-MM') === thisDate.format('YYYY-MM'))
          this.datesInMonths[idx].dates.push(thisDate)
          theDate.add(1, 'days')
        }
      }
      this.sendUpdate()
    },
    isInRange (date) {
      return (date.isBetween(this.fromDate.clone().startOf('day'), this.toDate.clone().endOf('day'), null, '[]'))
    },
    isWeekend (date) {
      return (date.isoWeekday() === 6 || date.isoWeekday() === 7)
    },
    isSelected (date) {
      return date.isSelected
    },
    toggle (date) {
      if (date.isSelectable) {
        date.isSelected = !date.isSelected
        if (!this.isDateSelected(date)) {
          this.selectedDays.push(date.simpleDate)
        } else {
          let index = this.selectedDays.indexOf(date.simpleDate)
          this.selectedDays.splice(index, 1)
        }
        this.sendToggle(date, date.isSelected)
      }
    },
    sendToggle (date, isSelected) {
      this.$emit('toggled', date.simpleDate, isSelected)
    },
    sendUpdate () {
      this.$emit('updated', {
        selectedDays: this.selectedDays,
        totalDaysCount: this.totalDaysCount,
        selectableDaysCount: this.selectableDaysCount,
        selectedDaysCount: this.selectedDaysCount
      })
    },
    isDateSelected (date) {
      return this.selectedDays.indexOf(moment(date).format('YYYY-MM-DD')) > -1
    }
  },
  mounted () {
    this.updateDates()
  }
}
</script>

<style>
@import "../../assets/css/variables.css";

.directDaySelector {
  display: flex;
  flex-direction: column;
}

.directDaySelector__month-and-dates {
  display: flex;
  margin-bottom: 1rem;
}

.directDaySelector .month {
  width: 90px;
  font-weight: bold;
}

.directDaySelector__month-and-dates__dates {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
}

.directDaySelector .day {
  cursor: pointer;
  float: left;
  text-align: center;
  user-select: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  line-height: 36px;
  color: var(--border-color);
  border: 1px solid var(--bg-grey);
  transition: transform 0.2s;
}

.directDaySelector .isInRange {
  color: var(--font-blue);
}

.directDaySelector .isWeekend {
  background-color: var(--bg-button);
}

.directDaySelector .isSelected {
  background-color: var(--blue);
  color: white;
}

.directDaySelector .isSelectable:hover {
  border: 1px solid var(--blue);
}

.directDaySelector .isSelectable:active {
  transform: scale(0.95);
}

.directDaySelector .day:not(.isSelectable) {
  cursor: default;
}
</style>



// WEBPACK FOOTER //
// DirectDaySelector.vue?23b72464